import CheckIcon from '@mui/icons-material/Check';
import ShareOutlinedIcon from '@mui/icons-material/ShareOutlined';
import { Box, Button, Card, CardContent, Chip, Divider, Stack, Typography } from '@mui/material';
import { convertToDateMX } from 'utils/helpers/convertToDateMX';
import CurrencyFormatter from 'utils/helpers/CurrencyFormatter';
import { DebtTicketsHook } from '../hooks/debtTicketsHook';

export default function DebtTickets({ billingCycleInfo }) {
  const { isCopied, handleCopyLink, calculateDaysRemaining } = DebtTicketsHook({
    billingCycleInfo,
  });

  return (
    <Card
      sx={{
        maxWidth: 550,
        margin: 'auto',
        borderRadius: 3,
        boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.05)',
        border: '1px solid',
        borderColor: 'grey.100',
        p: 1,
      }}
    >
      <CardContent sx={{ p: 3 }}>
        {/* Header */}
        <Box display='flex' justifyContent='center' alignItems='center' position='relative' mb={3}>
          <Typography variant='subtitle1' fontWeight='700' color='text.primary'>
            Detalle de Adeudo - {billingCycleInfo?.billing_cycle_type || 'N/A'}
          </Typography>
          <Chip
            icon={
              billingCycleInfo?.billing_cycle_status === 'ACTIVO' ? (
                <CheckIcon
                  sx={{
                    color: '#2e7d32 !important',
                    fontSize: '1rem',
                  }}
                />
              ) : null
            }
            label={billingCycleInfo?.billing_cycle_status}
            size='small'
            sx={{
              position: 'absolute',
              right: 0,
              bgcolor: billingCycleInfo?.billing_cycle_status === 'ACTIVO' ? '#e8f5e9' : '#f5e8e8',
              color: billingCycleInfo?.billing_cycle_status === 'ACTIVO' ? '#2e7d32' : '#da3c27',
              fontWeight: '700',
              borderRadius: 1.5,
              px: 0.5,
            }}
          />
        </Box>

        <Divider sx={{ mb: 3, borderColor: 'grey.200' }} />

        {/* Cifras principales */}
        <Stack
          direction='row'
          divider={<Divider orientation='vertical' flexItem sx={{ borderColor: 'grey.200' }} />}
          spacing={2}
          mb={3}
        >
          <Box
            flex={1}
            display='flex'
            flexDirection='column'
            alignItems='center'
            justifyContent='center'
          >
            <Typography variant='body2' color='text.secondary' mb={1}>
              Monto Adeudado
            </Typography>
            <Typography variant='h5' fontWeight='800' color='text.primary'>
              {CurrencyFormatter({ amount: billingCycleInfo?.billed_amount || 0 })} MXN
            </Typography>
          </Box>

          <Box
            flex={1}
            display='flex'
            flexDirection='column'
            alignItems='center'
            justifyContent='center'
          >
            <Typography variant='body2' color='text.secondary' mb={1} mt={1}>
              Vencimiento Próximo
            </Typography>
            <Typography variant='h6' fontWeight='700' color='text.primary'>
              {convertToDateMX(billingCycleInfo?.billing_cycle_grace_expires_at) || 'N/A'}
            </Typography>
            <Typography variant='body2' color='text.secondary'>
              {calculateDaysRemaining(billingCycleInfo?.billing_cycle_grace_expires_at) || ''}
            </Typography>
          </Box>
        </Stack>

        {/* Caja de detalles expandida */}
        <Box position='relative' border={1} borderColor='grey.300' borderRadius={2} p={3} mb={4}>
          <Stack spacing={1.5}>
            <Typography variant='body2' color='text.primary' fontWeight='500'>
              Fecha de inicio de adeudo:{' '}
              {convertToDateMX(billingCycleInfo?.billing_cycle_start_at) || 'N/A'}
            </Typography>
            <Typography variant='body2' color='text.primary' fontWeight='500'>
              Fecha de corte: {convertToDateMX(billingCycleInfo?.billing_cycle_end_at) || 'N/A'}
            </Typography>
            <Typography variant='body2' color='text.primary' fontWeight='500'>
              Fecha limite de pago:{' '}
              {convertToDateMX(billingCycleInfo?.billing_cycle_grace_expires_at) || 'N/A'}
            </Typography>
            <Typography variant='body2' color='text.primary' fontWeight='500'>
              UUID: {billingCycleInfo?.uuid || 'N/A'}
            </Typography>
          </Stack>
        </Box>

        {/* Botón de acción */}
        <Box display='flex' justifyContent='center'>
          <Button
            variant='contained'
            onClick={handleCopyLink}
            startIcon={isCopied ? <CheckIcon /> : <ShareOutlinedIcon />}
            disabled={!billingCycleInfo?.payment_link?.[0]}
            sx={{
              bgcolor: '#512da8',
              textTransform: 'none',
              fontWeight: '600',
              fontSize: '1rem',
              borderRadius: 2.5,
              px: 4,
              py: 1.2,
              boxShadow: 'none',
              '&:hover': {
                bgcolor: '#512da8',
                boxShadow: 'none',
              },
              '&.Mui-disabled': {
                bgcolor: 'grey.300',
                color: 'grey.500',
              },
            }}
          >
            {isCopied ? '¡Enlace copiado!' : 'Compartir link de pago'}
          </Button>
        </Box>
      </CardContent>
    </Card>
  );
}
